import { useEffect, useState } from "react";
import { FlameMark } from "../components/Logo";
import { formatINR } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["PLACED", "CONFIRMED", "DISPATCHED", "DELIVERED", "CANCELLED"];
const CATEGORY_OPTIONS = ["sparklers", "flowerpots", "chakkar", "rockets", "bombs", "fancy", "kids"];
const STATUS_STYLE = {
  PLACED: "border-ember/50 text-ember",
  CONFIRMED: "border-sky-400/50 text-sky-300",
  DISPATCHED: "border-purple-400/50 text-purple-300",
  DELIVERED: "border-discount-green/50 text-discount-green",
  CANCELLED: "border-crimson/50 text-crimson",
};

let onSessionExpired = null;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function authedFetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (res.status === 401) {
    const refreshed = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (refreshed.ok) {
      const retry = await fetch(`${API}${path}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        ...options,
      });
      if (retry.ok) return retry.json();
    }
    if (onSessionExpired) onSessionExpired();
    throw new Error("Your session expired. Please log in again.");
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(formatApiErrorDetail(data?.detail));
  return data;
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(formatApiErrorDetail(data?.detail));
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-starlight flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-ember/25 bg-card p-8"
      >
        <div className="flex items-center gap-3 justify-center">
          <FlameMark className="h-10 w-10" />
          <div>
            <p className="font-display font-bold tracking-[0.18em] text-starlight text-lg">UTSAV</p>
            <p className="font-mono text-[10px] tracking-[0.42em] text-ember">SHOP CONSOLE</p>
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-muted-night">Owner access only — sign in to manage orders.</p>

        <label className="block mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Email</label>
        <input
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@utsavcrackers.in"
          className="mt-1.5 w-full rounded-xl border border-starlight/15 bg-white/5 px-4 py-3 text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70"
        />

        <label className="block mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Password</label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1.5 w-full rounded-xl border border-starlight/15 bg-white/5 px-4 py-3 text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70"
        />

        {error && (
          <p className="mt-4 rounded-xl border border-crimson/40 bg-crimson/10 px-4 py-3 text-xs text-starlight" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-full bg-ember py-3.5 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian hover:bg-marigold transition-colors disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <a href="/" className="mt-4 block text-center font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night hover:text-ember">
          ← Back to store
        </a>
      </form>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState(null);

  const load = (f = filter) => {
    authedFetch(`/admin/orders${f ? `?status=${f}` : ""}`)
      .then((d) => {
        setOrders(d);
        setError(null);
      })
      .catch((e) => setError(e.message));
  };

  const loadSummary = () => {
    authedFetch("/admin/summary").then(setSummary).catch(() => {});
  };

  useEffect(() => {
    load(filter);
    loadSummary();
  }, [filter]);

  const setStatus = async (orderNumber, status) => {
    try {
      await authedFetch(`/admin/orders/${orderNumber}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      load();
      loadSummary();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-summary">
          <div className="rounded-2xl border border-starlight/10 bg-card p-4">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Today's orders</p>
            <p className="mt-2 font-mono text-2xl font-bold text-starlight" data-testid="summary-today-orders">
              {summary.today_orders}
            </p>
          </div>
          <div className="rounded-2xl border border-starlight/10 bg-card p-4">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Today's COD value</p>
            <p className="mt-2 font-mono text-2xl font-bold text-ember" data-testid="summary-today-revenue">
              {formatINR(summary.today_revenue)}
            </p>
          </div>
          <div className="rounded-2xl border border-starlight/10 bg-card p-4">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Needs action</p>
            <p
              className={`mt-2 font-mono text-2xl font-bold ${summary.needs_action > 0 ? "text-crimson" : "text-discount-green"}`}
              data-testid="summary-needs-action"
            >
              {summary.needs_action}
            </p>
          </div>
          <div className="rounded-2xl border border-starlight/10 bg-card p-4">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Best sellers</p>
            <p className="mt-2 text-xs text-muted-night leading-relaxed" data-testid="summary-top-products">
              {summary.top_products.length
                ? summary.top_products.map((t) => `${t.name} ×${t.qty}`).join(" • ")
                : "No sales yet"}
            </p>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {["", ...STATUSES].map((s) => (
          <button
            key={s || "all"}
            data-testid={`admin-order-filter-${s || "all"}`}
            onClick={() => setFilter(s)}
            className={`rounded-full px-4 py-1.5 font-mono text-[10px] tracking-[0.16em] uppercase border transition-colors ${
              filter === s ? "bg-ember text-obsidian border-ember font-bold" : "border-starlight/15 text-muted-night hover:border-ember/60"
            }`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {error && <p className="mt-4 rounded-xl border border-crimson/40 bg-crimson/10 px-4 py-3 text-sm text-starlight">{error}</p>}

      {orders === null && <p className="mt-8 font-mono text-xs text-muted-night">Loading orders…</p>}

      {orders && orders.length === 0 && (
        <div className="mt-10 rounded-2xl border border-dashed border-starlight/15 py-16 text-center">
          <p className="font-display text-xl text-starlight">No orders here yet</p>
          <p className="mt-2 text-sm text-muted-night">Orders from the website will appear the moment customers place them.</p>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {orders?.map((o) => (
          <div
            key={o.order_number}
            data-testid={`admin-order-card-${o.order_number}`}
            className="rounded-2xl border border-starlight/10 bg-card p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-mono text-lg font-bold text-ember">{o.order_number}</p>
                <p className="font-mono text-[10px] text-muted-night mt-0.5">
                  {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full border px-3 py-1 font-mono text-[10px] font-bold tracking-[0.14em] ${STATUS_STYLE[o.status] || ""}`}>
                  {o.status}
                </span>
                <select
                  data-testid={`admin-order-status-${o.order_number}`}
                  value={o.status}
                  onChange={(e) => setStatus(o.order_number, e.target.value)}
                  className="rounded-lg border border-starlight/15 bg-obsidian px-3 py-2 text-xs text-starlight outline-none focus:border-ember/70 cursor-pointer"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Customer</p>
                <p className="mt-1 font-semibold text-starlight">{o.customer.name}</p>
                <p className="text-muted-night">{o.customer.phone}</p>
                <p className="text-muted-night mt-1 leading-relaxed">
                  {o.customer.address}
                  {o.customer.landmark ? ` (${o.customer.landmark})` : ""} — {o.customer.pincode}
                </p>
                {o.customer.delivery_note && (
                  <p className="mt-1 text-xs text-ember/80">Note: {o.customer.delivery_note}</p>
                )}
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">
                  Items ({o.items.reduce((s, i) => s + i.qty, 0)})
                </p>
                <p className="mt-1 text-muted-night leading-relaxed">
                  {o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}
                </p>
                <p className="mt-2 font-mono text-base font-bold text-ember">
                  {formatINR(o.total)} <span className="text-[10px] text-muted-night font-normal">payable on delivery</span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = { name: "", tamil: "", category: "sparklers", content: "", desc: "", image: "", mrp: "", price: "", sound: 1, rating: 4.5, badge: "" };

function ProductsTab() {
  const [products, setProducts] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const load = () => authedFetch("/admin/products").then(setProducts).catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setEditing("new");
    setForm(EMPTY_PRODUCT);
    setToast(null);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, badge: p.badge || "" });
    setToast(null);
  };

  const save = async (e) => {
    e.preventDefault();
    const price = parseInt(form.price, 10);
    const mrp = parseInt(form.mrp, 10);
    if (!form.name.trim() || !price || !mrp || mrp < price) {
      setToast("Fill name, and make sure MRP is greater than or equal to the sale price.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price, mrp, sound: parseInt(form.sound, 10) || 1, rating: parseFloat(form.rating) || 4.5, badge: form.badge || null };
      if (editing === "new") {
        await authedFetch("/admin/products", { method: "POST", body: JSON.stringify(payload) });
        setToast("Product added — it's live on the store now.");
      } else {
        await authedFetch(`/admin/products/${editing}`, { method: "PATCH", body: JSON.stringify(payload) });
        setToast("Product updated.");
      }
      setEditing(null);
      setForm(EMPTY_PRODUCT);
      load();
    } catch (err) {
      setToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the store?`)) return;
    try {
      await authedFetch(`/admin/products/${p.id}`, { method: "DELETE" });
      setToast(`${p.name} removed.`);
      load();
    } catch (err) {
      setToast(err.message);
    }
  };

  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/upload-image`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      setForm((f) => ({ ...f, image: data.image }));
    } catch (err) {
      setToast(err.message);
    } finally {
      setUploading(false);
    }
  };

  const inputCls =
    "mt-1 w-full rounded-lg border border-starlight/15 bg-white/5 px-3 py-2 text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-night">Add new crackers or edit any product's details — changes go live instantly.</p>
        <button
          data-testid="admin-add-product-button"
          onClick={startAdd}
          className="rounded-full bg-ember px-6 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-obsidian hover:bg-marigold transition-colors"
        >
          + Add product
        </button>
      </div>

      {toast && (
        <p className="mt-4 rounded-xl border border-ember/40 bg-ember/10 px-4 py-3 text-sm text-starlight" data-testid="admin-product-toast">
          {toast}
        </p>
      )}
      {error && <p className="mt-4 rounded-xl border border-crimson/40 bg-crimson/10 px-4 py-3 text-sm text-starlight">{error}</p>}

      {editing && (
        <form onSubmit={save} className="mt-5 rounded-2xl border border-ember/30 bg-card p-5 space-y-4" data-testid="admin-product-form">
          <p className="font-display text-lg font-bold text-starlight">{editing === "new" ? "Add a new cracker" : "Edit product"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Name (English)</label>
              <input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 15 cm Gold Sparklers" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Subtitle (optional)</label>
              <input data-testid="product-form-tamil" value={form.tamil} onChange={(e) => setForm({ ...form, tamil: e.target.value })} placeholder="Short label shown under the name" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Category</label>
              <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Pack size</label>
              <input data-testid="product-form-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="e.g. 10 pcs per box" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Price (₹)</label>
              <input data-testid="product-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/\D/g, "") })} placeholder="e.g. 60" inputMode="numeric" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">MRP (₹)</label>
              <input data-testid="product-form-mrp" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value.replace(/\D/g, "") })} placeholder="e.g. 240" inputMode="numeric" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Sound level (1–5)</label>
              <input data-testid="product-form-sound" value={form.sound} onChange={(e) => setForm({ ...form, sound: e.target.value.replace(/\D/g, "").slice(0, 1) })} inputMode="numeric" className={inputCls} />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Badge (optional)</label>
              <select data-testid="product-form-badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls}>
                <option value="">None</option>
                <option value="BESTSELLER">Bestseller</option>
                <option value="NEW">New</option>
                <option value="PREMIUM">Premium</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Product image</label>
            <div className="mt-1 flex items-center gap-3">
              <input data-testid="product-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="/images/products/sparkler-gold.jpeg" className={`${inputCls} flex-1`} />
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-ember/50 px-4 py-2 font-mono text-[10px] font-bold uppercase text-ember hover:bg-ember hover:text-obsidian transition-colors whitespace-nowrap">
                {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={handleImageFile} className="hidden" data-testid="product-form-file" />
              </label>
            </div>
            {(preview || form.image) && (
              <img src={preview || form.image} alt="preview" className="mt-2 h-16 w-16 rounded-lg object-cover border border-starlight/15" />
            )}
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Description (optional)</label>
            <input data-testid="product-form-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="One line about the cracker" className={inputCls} />
          </div>
          <div className="flex gap-3">
            <button
              data-testid="product-form-save"
              type="submit"
              disabled={saving}
              className="rounded-full bg-ember px-8 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-obsidian hover:bg-marigold transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : editing === "new" ? "Add product" : "Save changes"}
            </button>
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_PRODUCT); }} className="rounded-full border border-starlight/15 px-6 py-2.5 font-mono text-[11px] uppercase text-muted-night hover:border-ember/60">
              Cancel
            </button>
          </div>
        </form>
      )}

      {products === null && <p className="mt-8 font-mono text-xs text-muted-night">Loading products…</p>}

      <div className="mt-6 space-y-2">
        {products?.map((p) => (
          <div
            key={p.id}
            data-testid={`admin-product-row-${p.id}`}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-starlight/10 bg-card px-4 py-3"
          >
            <img src={p.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-starlight">{p.name}</p>
              <p className="font-mono text-[10px] text-muted-night uppercase tracking-[0.14em]">
                {p.category} • ₹{p.price} / MRP ₹{p.mrp}
              </p>
            </div>
            <button
              data-testid={`admin-edit-product-${p.id}`}
              onClick={() => startEdit(p)}
              className="rounded-lg border border-ember/50 px-4 py-1.5 font-mono text-[10px] font-bold tracking-[0.14em] uppercase text-ember hover:bg-ember hover:text-obsidian transition-colors"
            >
              Edit
            </button>
            <button
              data-testid={`admin-delete-product-${p.id}`}
              onClick={() => remove(p)}
              className="rounded-lg border border-crimson/50 px-3 py-1.5 font-mono text-[10px] font-bold tracking-[0.14em] uppercase text-crimson hover:bg-crimson hover:text-starlight transition-colors"
              aria-label={`Remove ${p.name}`}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authedFetch("/settings").then(setForm).catch(() => setForm({ address: "", phone: "", whatsapp: "", email: "" }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setToast(null);
    try {
      const saved = await authedFetch("/admin/settings", { method: "PUT", body: JSON.stringify(form) });
      setForm(saved);
      setToast("Shop details saved — they now appear on the store footer.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <p className="mt-8 font-mono text-xs text-muted-night">Loading settings…</p>;

  const inputCls =
    "mt-1.5 w-full rounded-xl border border-starlight/15 bg-white/5 px-4 py-3 text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70";

  return (
    <form onSubmit={save} className="max-w-xl space-y-5" data-testid="admin-settings-form">
      <p className="text-sm text-muted-night">
        These details show on the store footer so customers know how to reach you. Leave blank to hide.
      </p>
      <div>
        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Shop address</label>
        <textarea
          data-testid="admin-settings-address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Utsav Crackers, 12 Main Road, Your City, State — 626123"
          rows={3}
          className={`${inputCls} resize-none`}
        />
      </div>
      <div>
        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Phone number</label>
        <input
          data-testid="admin-settings-phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15) })}
          placeholder="98765 43210"
          className={inputCls}
        />
      </div>
      <div>
        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">WhatsApp number</label>
        <input
          data-testid="admin-settings-whatsapp"
          value={form.whatsapp}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/[^\d+]/g, "").slice(0, 15) })}
          placeholder="98765 43210"
          className={inputCls}
        />
      </div>
      <div>
        <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-night">Email address</label>
        <input
          data-testid="admin-settings-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          className={inputCls}
        />
      </div>
      {toast && (
        <p className="rounded-xl border border-discount-green/40 bg-discount-green/10 px-4 py-3 text-sm text-starlight" data-testid="admin-settings-toast">
          {toast}
        </p>
      )}
      <button
        data-testid="admin-save-settings-button"
        type="submit"
        disabled={busy}
        className="rounded-full bg-ember px-10 py-3.5 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian hover:bg-marigold transition-colors disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save details"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    onSessionExpired = () => setUser(false);
    authedFetch("/auth/me").then(setUser).catch(() => setUser(false));
    return () => {
      onSessionExpired = null;
    };
  }, []);

  if (user === null) {
    return (
      <div className="min-h-screen bg-obsidian text-starlight flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-night">Opening the console…</p>
      </div>
    );
  }

  if (user === false) return <Login onSuccess={setUser} />;

  return (
    <div className="min-h-screen bg-obsidian text-starlight" data-testid="admin-dashboard">
      <header className="border-b border-starlight/10 bg-card/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlameMark className="h-8 w-8" />
            <div>
              <p className="font-display font-bold tracking-[0.18em] text-starlight">UTSAV CONSOLE</p>
              <p className="font-mono text-[10px] text-muted-night">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:block rounded-full border border-starlight/15 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
            >
              View store
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={async () => {
                await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
                setUser(false);
              }}
              className="rounded-full border border-crimson/50 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-crimson hover:bg-crimson hover:text-starlight transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-8 py-10">
        <div className="flex gap-2">
          {[
            { id: "orders", label: "Orders" },
            { id: "products", label: "Prices" },
            { id: "settings", label: "Shop Details" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-6 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] uppercase transition-colors ${
                tab === t.id ? "bg-ember text-obsidian" : "border border-starlight/15 text-muted-night hover:border-ember/60 hover:text-ember"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "orders" && <OrdersTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}
